// Elation lab results — read-only accessors over the labs-review task row.
// The stamp has ONE writer: WF72's decide region via WF17's `labs` change key, pinned by S-129.
// Spec: docs/specs/2026-09-01-elation-lab-sync.md

import { isClosedStatus, readTaskContext, templateCodeOf } from './task';
import {
  LAB_RELEASE_WINDOW_MS, labReleaseMarkerOk, labSameInstant, labNoteClean, labNoteCurrent, labNoteFrozen,
  type LabHoldReason, type LabReleaseState,
} from './labResults';

const LABS_REVIEW_TEMPLATE = 'onb_baseline_labs_review';

export interface TaskLike {
  status?: string | null;
  context_json?: unknown;
  [key: string]: unknown;
}

export interface LabsResult {
  /**
   * 'partial'  — some specimens are back, the panel is incomplete.
   * 'resulted' — every specimen is back.
   * An unknown state is ignored, not guessed at.
   */
  state: 'partial' | 'resulted';
  /** Specimens back / total, for the count-led copy. Null on a stamp written before 2026-09-04. */
  gridsFinal: number | null;
  gridsTotal: number | null;
  /** Bounded, LAB-SUPPLIED free text. Display only — never a key, never a lookup. */
  pendingTests: string[];
  /** When the results were filed, or when we first saw them — see `datedFrom`. */
  at: Date | null;
  /**
   * 'elation'  — Elation's own resolution date: this is when the results were filed.
   * 'detected' — the order was fulfilled but carried no date, so this is when WE noticed.
   * The distinction is the whole reason the workflow carries it: rendering a detection time under
   * the word "filed" asserts something we cannot prove.
   */
  datedFrom: 'elation' | 'detected';
}

/**
 * The gate both surfaces use.
 *
 * Deliberately the CLOSED-set complement rather than careUpdate.ts's three-status open set: a
 * review task whose status has flipped to `overdue` is exactly the case where "results are in"
 * matters most — late AND waiting — and an allow-list of open statuses hides the strip there.
 * (careUpdate.ts carries that same gap on share tasks; flagged, not silently fixed here.)
 */
export function isLabsReviewTask(task: TaskLike | null | undefined): boolean {
  return !!task && templateCodeOf(task) === LABS_REVIEW_TEMPLATE && !isClosedStatus(task.status);
}

/**
 * The `context_json.labs` stamp, or null.
 *
 * Fails OPEN on an unrecognised state — the card renders normally rather than on a guess.
 * `elation_order_id` is a join key and is NEVER returned: same rule as `ehr_appointment_id` in
 * task.ts, so it cannot reach the DOM by accident.
 */
export function labsResultOf(task: TaskLike | null | undefined): LabsResult | null {
  const raw = readTaskContext(task?.context_json)?.labs;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as {
    state?: unknown; resulted_at?: unknown; resulted_at_source?: unknown;
    grids_final?: unknown; grids_total?: unknown; pending_tests?: unknown;
  };
  const state = String(r.state ?? '');
  if (state !== 'resulted' && state !== 'partial') return null;
  const t = Date.parse(String(r.resulted_at ?? ''));
  const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : null);
  return {
    state,
    gridsFinal: num(r.grids_final),
    gridsTotal: num(r.grids_total),
    // Bounded again on the way OUT: the workflow caps it, but this is untrusted lab text reaching
    // the DOM, and a stamp written by an older or hand-edited row is not covered by that cap.
    pendingTests: Array.isArray(r.pending_tests)
      ? r.pending_tests.slice(0, 8).map((x) => String(x).slice(0, 64)).filter(Boolean)
      : [],
    at: Number.isFinite(t) ? new Date(t) : null,
    datedFrom: String(r.resulted_at_source ?? '') === 'elation' ? 'elation' : 'detected',
  };
}

/**
 * Month-Day, rendered in UTC. Null when the stamp carried no usable date.
 *
 * UTC is not cosmetic: on the 'elation' branch `resulted_at` is a calendar DATE the workflow anchors
 * at noon Z, and a date rendered in a negative-offset zone shows the day before (the `birthdate`
 * class in withings-conventions.md). Noon absorbs every US offset today, but the 'detected' branch
 * carries a real instant — a tick between 00:00 and 05:00 Z would render the previous day to an
 * Eastern coordinator. Nothing app-side pins that anchor, so don't rely on it.
 */
export function labsResultDate(result: LabsResult | null): string | null {
  if (!result?.at) return null;
  return result.at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export interface LabRelease {
  state: LabReleaseState;
  since: string;
  at: string | null;
  reason: LabHoldReason | null;
  // A request WF72 never moved on (switched off after the Done, or down): no longer in flight.
  stale: boolean;
  // Tests the released card left off because the lab sent two values for them; lab text, display only.
  withheld: string[];
}

// WF72 reads a request for 2 h past its window so it can write 'expired'; after that nothing will.
const RELEASE_STALE_AFTER_MS = LAB_RELEASE_WINDOW_MS + 2 * 3600000;

// The release marker on a COMPLETED review (spec 2026-10-08 §0), minus its join keys. A requested or
// held marker from an earlier Done is dead (WF72 ignores it), so it never reads as still in flight.
export function labReleaseOf(task: TaskLike | null | undefined, nowMs: number = Date.now()): LabRelease | null {
  if (!task || templateCodeOf(task) !== LABS_REVIEW_TEMPLATE || task.status !== 'completed') return null;
  const m = readTaskContext(task.context_json)?.lab_release;
  if (!labReleaseMarkerOk(m)) return null;
  const pending = m.state === 'requested' || m.state === 'held';
  if (pending && task.completed_at != null && !labSameInstant(m.since, task.completed_at)) return null;
  return {
    state: m.state,
    since: m.since,
    at: m.at ?? null,
    reason: m.reason ?? null,
    stale: pending && nowMs - Date.parse(m.since) > RELEASE_STALE_AFTER_MS,
    withheld: m.state === 'released' && m.withheld ? [...m.withheld] : [],
  };
}

export interface LabNote {
  text: string;
  // The author's full name, for the care team; the patient's card shows the first name only.
  author: string | null;
  at: string;
  sentAt: string | null;
}

// The care team's note on the review (spec 2026-10-14-lab-results-care-team-note §3.3): a sent one always (the
// patient has it), an unsent one only on the completed row of the Done that wrote it. Fails closed.
export function labNoteOf(task: TaskLike | null | undefined): LabNote | null {
  if (!task || templateCodeOf(task) !== LABS_REVIEW_TEMPLATE) return null;
  const n = labNoteCurrent({
    status: typeof task.status === 'string' ? task.status : null,
    completed_at: typeof task.completed_at === 'string' ? task.completed_at : null,
    context_json: task.context_json,
  });
  if (!n) return null;
  const by = n.by ?? {};
  // Cleaned like the note: no bidi or zero-width characters in a name shown to staff.
  const author = [by.firstname, by.lastname].map((s) => labNoteClean(s)).filter(Boolean).join(' ') || null;
  return {
    text: n.text, author, at: n.at, sentAt: n.sent_at ?? null,
  };
}

export type LabNoteShare = 'sent' | 'pending' | 'not_sent';

// Claims only what the task proves: sent when WF72 reported the note rode the card, pending while this Done's
// release still waits. Never "will go out": LAB_NOTE_RELEASE may be off.
export function labNoteShareOf(task: TaskLike | null | undefined, note: LabNote, nowMs: number = Date.now()): LabNoteShare {
  if (note.sentAt) return 'sent';
  const m = readTaskContext(task?.context_json)?.lab_release;
  const waiting = labReleaseMarkerOk(m) && (m.state === 'requested' || m.state === 'held') && labSameInstant(m.since, note.at);
  // A stale request is what the release line calls "not confirmed": the note cannot claim it is still coming.
  return waiting && !labReleaseOf(task, nowMs)?.stale ? 'pending' : 'not_sent';
}

export type LabNoteOutcome = 'not_saved' | 'not_done' | 'unconfirmed';

// What the dialog says after a completion with a note: the task was read back, so each line states only what is known.
export const LAB_NOTE_OUTCOME_COPY: Record<LabNoteOutcome, string> = {
  not_saved: 'The review is marked Done, but the note was not saved. Copy it into the chat instead.',
  not_done: 'The review was not marked Done. Try again.',
  unconfirmed: 'Sent, but the review does not show as Done yet. Check the task in a minute before sending again: it may already have gone out with your note.',
};

export function labNoteOutcomeOf(r: { completed: boolean; noteSaved: boolean; unconfirmed?: boolean }): LabNoteOutcome | null {
  if (r.completed) return r.noteSaved ? null : 'not_saved';
  return r.unconfirmed ? 'unconfirmed' : 'not_done';
}

// A card already went out for this review: no new note can ride it (WF17 refuses one too). Read on the raw
// context, since the dialog opens on an open or reopened row.
export function labCardAlreadySent(task: TaskLike | null | undefined): boolean {
  const ctx = readTaskContext(task?.context_json);
  if (labNoteFrozen(ctx)) return true;
  const m = ctx?.lab_release;
  return labReleaseMarkerOk(m) && m.state === 'released';
}

const HOLD_PHRASES: Record<LabHoldReason, string> = {
  critical_open: 'a critical lab value is still open',
  critical_unreviewed: 'a critical value needs a clinician first',
  critical_read_degraded: 'could not check for critical values',
  no_consent: 'no consent to message the patient',
  guard_degraded: 'could not check what was already sent',
  foreign_report: 'the lab report does not match this patient',
  truncated: 'too many lab reports to read safely',
  conflict: 'the lab sent two different values for one test',
  flag_missing: 'a result has no flag from the lab',
  empty: 'nothing to share',
  nothing_final: 'no final results yet',
  unnamed: 'a result has no name',
  too_many: 'too many results for one card',
  unparseable: 'the card could not be built',
  order_cancelled: 'the lab order was cancelled',
  no_grids: 'no lab report found',
  window: 'the sharing window has passed',
};

// Unknown or missing reason reads as a bare "Held": never a guess at why.
export function labHoldPhrase(reason: string | null | undefined): string | null {
  return reason && Object.hasOwn(HOLD_PHRASES, reason) ? HOLD_PHRASES[reason as LabHoldReason] : null;
}

// A real instant, so the viewer's own zone (unlike labsResultDate's noon-anchored calendar date).
export function labReleaseWhen(iso: string | null | undefined, timeZone?: string): string | null {
  const t = Date.parse(String(iso ?? ''));
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short', ...(timeZone ? { timeZone } : {}),
  });
}
